import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo2.png';

const LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'tr', label: 'Türkçe',     flag: '🇹🇷' },
  { code: 'ru', label: 'Русский',    flag: '🇷🇺' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'pl', label: 'Polski',     flag: '🇵🇱' },
  { code: 'pt', label: 'Português',  flag: '🇵🇹' },
  { code: 'hu', label: 'Magyar',     flag: '🇭🇺' },
  { code: 'zh', label: '中文',        flag: '🇨🇳' },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [click, setClick] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const handleClick = () => setClick(!click);
  const closeMobileMenu = () => setClick(false);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language)
    || LANGUAGES.find(l => l.code === i18n.language?.split('-')[0])
    || LANGUAGES[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <nav className="bg-black h-16 fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 h-full">
        <div className="flex items-center justify-between h-full">

          {/* Logo */}
          <Link
            to="/"
            className="text-white text-2xl font-bold flex items-center hover:opacity-80 bg-transparent clickable"
            onClick={closeMobileMenu}
          >
            <img src={logo} alt="ConfesZ Logo" className="h-20 clickable" />
          </Link>

          {/* Mobile: hamburger */}
          <div className="md:hidden flex items-center gap-3">
            {/* Language switcher (mobile) */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="bg-transparent text-white flex items-center gap-1 px-2 py-1 rounded hover:opacity-80 clickable text-base"
                aria-label="Select language"
              >
                <span>{currentLang.flag}</span>
                <span className="text-xs text-gray-400">{currentLang.code.toUpperCase()}</span>
                <svg className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-10 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 min-w-[160px] py-1 overflow-hidden">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm transition-colors duration-150
                        ${lang.code === currentLang.code
                          ? 'bg-yellow-400 text-black font-semibold'
                          : 'text-white hover:bg-zinc-800'}`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleClick}
              className="bg-transparent text-white p-2 rounded-md hover:opacity-80 clickable"
            >
              <i className={click ? 'fas fa-times' : 'fas fa-bars'}></i>
            </button>
          </div>

          {/* Desktop nav links + language switcher */}
          <div className={`${
              click ? 'flex' : 'hidden'
            } md:flex absolute md:relative top-16 md:top-0 left-0 w-full md:w-auto
            bg-black md:bg-transparent flex-col md:flex-row items-start md:items-center
            z-50`}
          >
            <Link to="/" className="clickable bg-transparent text-white hover:text-yellow-400 transition-colors duration-300 px-4 py-2 w-full md:w-auto" onClick={closeMobileMenu}>
              {t('navbar.home')}
            </Link>
            <Link to="/downloads" className="clickable bg-transparent text-white hover:text-yellow-400 transition-colors duration-300 px-4 py-2 w-full md:w-auto" onClick={closeMobileMenu}>
              {t('navbar.downloads')}
            </Link>
            <Link to="/about" className="clickable bg-transparent text-white hover:text-yellow-400 transition-colors duration-300 px-4 py-2 w-full md:w-auto" onClick={closeMobileMenu}>
              {t('navbar.about')}
            </Link>
            <Link to="/contact" className="clickable bg-transparent text-white hover:text-yellow-400 transition-colors duration-300 px-4 py-2 w-full md:w-auto" onClick={closeMobileMenu}>
              {t('navbar.contact')}
            </Link>

            {/* Language Switcher — Desktop */}
            <div className="hidden md:block relative ml-2" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="bg-transparent text-white flex items-center gap-1.5 px-3 py-2 rounded hover:text-yellow-400 transition-colors duration-300 clickable"
                aria-label="Select language"
              >
                <span className="text-lg leading-none">{currentLang.flag}</span>
                <span className="text-sm font-medium">{currentLang.code.toUpperCase()}</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {langOpen && (
                <div className="absolute right-0 top-12 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 min-w-[165px] py-1 overflow-hidden">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left flex items-center gap-2.5 px-4 py-2 text-sm transition-colors duration-150
                        ${lang.code === currentLang.code
                          ? 'bg-yellow-400 text-black font-semibold'
                          : 'text-white hover:bg-zinc-800'}`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;